import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import Modal from 'components/shared/Modal'
import Text from 'components/shared/Text'
import Calendar from 'components/shared/Calendar'
import {
    ModalHeader,
    ModalFooter,
} from 'components/global/CalendarModal/styles'
import { formatDates } from 'helpers/dates'
import { useResolutions } from 'store/resolutions'
import { FormattedMessage } from 'react-intl'
import moment, { Moment } from 'moment'
import { useEffect, useState } from 'react'
import { useListAccommodationAvailableDaysQuery } from 'store/api-slice/hooks'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'
import {
    removeAllBungalows,
    removeBookingPersonDetails,
    removeGuestsToAssign,
    setBungalowsVisible,
    setDates,
} from 'store/accommodation-slice'
import { useAccommodation } from 'store/accommodation-slice/hooks'
import { closeModalActions, pushDataLayer } from 'helpers/helpers'
import { CalendarLoader } from 'components/shared/Calendar/components/CalendarLoader'
import CalendarLegendItem from 'components/global/CalendarModal/components/CalendarModalLegend/components/CalendarLegendItem'

interface CalendarProps {
    isOpened: boolean
    close: () => void
}

const AccommodationCalendarModal = ({ isOpened, close }: CalendarProps) => {
    const dispatch = useAppDispatch()
    const { language, isCreatorAccommodation } = useApp()
    const [isDisabledCalendar, setIsDisabledCalendar] = useState(true)
    const [isConfirmed, setIsConfirmed] = useState(false)
    const [unconfirmedDates, setUnconfirmedDates] = useState<string[]>([])
    const { isMobile } = useResolutions()
    const { data, isFetching } = useListAccommodationAvailableDaysQuery(
        {
            _locale: language,
        },
        { skip: !isOpened }
    )
    const cartAccommodation = useAppSelector(
        (state) => state.cart.items.accommodation
    )
    const { areBungalowsVisible, dates } = useAppSelector(
        (state) => state.accommodation
    )
    const { removeAccommodationFromCart } = useAccommodation(
        cartAccommodation?.productId
    )

    const handleSetDates = (dates: Moment[]) => {
        if (dates.length > 1) {
            const fullDatesRange = []

            for (
                let date = moment(dates[0]);
                date.isSameOrBefore(dates[1]);
                date.add(1, 'days')
            ) {
                fullDatesRange.push(date.format('YYYY-MM-DD'))
            }
            setUnconfirmedDates(fullDatesRange)
        } else {
            setUnconfirmedDates([dates[0].format('YYYY-MM-DD')])
        }
        if (!isMobile) {
            setIsConfirmed(true)
        }
    }
    const confirmDates = async () => {
        const closeModal = () => {
            setUnconfirmedDates([])
            setIsDisabledCalendar(false)
            setIsConfirmed(false)
            clearTimeout(timeout)
            closeModalActions(false)
            close()
        }

        if (cartAccommodation) await removeAccommodationFromCart()

        dispatch(removeGuestsToAssign())
        dispatch(removeAllBungalows())
        dispatch(removeBookingPersonDetails())

        if (areBungalowsVisible) {
            dispatch(setBungalowsVisible(false))
        }

        dispatch(setDates(unconfirmedDates))

        const timeout = setTimeout(closeModal, 500)

        // < analytics
        isCreatorAccommodation &&
            pushDataLayer({
                event: 'funnelStep',
                event_category: 'accommodation',
                event_action: 'arrival_date_selected',
                event_label: '',
            })
        // analytics >
    }

    useEffect(() => {
        if (isConfirmed) {
            setIsDisabledCalendar(true)
            confirmDates()
        }
    }, [isConfirmed])

    useEffect(() => {
        if (isFetching || !data) return
        setIsDisabledCalendar(false)
    }, [isFetching, data])

    return (
        <Modal
            isOpened={isOpened}
            maxWidth={786}
            onClose={() => {
                close()
                setUnconfirmedDates([])
            }}
            isMobileFullscreen={true}
            testIdPrefix="accommo-calendar"
        >
            <>
                <ModalHeader isFullWidth>
                    <Text variant="body" mb={4} color="grayscale_900">
                        <FormattedMessage
                            id="global.calendar.selected_date_label"
                            defaultMessage="Data pobytu"
                        />
                    </Text>

                    {unconfirmedDates.length > 0 ? (
                        <Box direction="row" gap={4}>
                            <Text variant="bodyLarge" color="secondary_500">
                                {formatDates(unconfirmedDates)}
                            </Text>
                            {unconfirmedDates.length > 1 && (
                                <Text variant="bodyLarge" color="grayscale_700">
                                    (
                                    <FormattedMessage
                                        id="global.plurals.nights"
                                        defaultMessage="{nights, plural, one {# noc} few {# noce} many {# nocy} other {# nocy}}"
                                        values={{
                                            nights: unconfirmedDates.length - 1,
                                        }}
                                    />
                                    )
                                </Text>
                            )}
                        </Box>
                    ) : (
                        <Text variant="bodyLarge" color="secondary_500">
                            <FormattedMessage
                                id="global.calendar.selected_date_placeholder"
                                defaultMessage="Wybierz dzień przyjazdu"
                            />
                        </Text>
                    )}
                </ModalHeader>
                {isFetching ? (
                    <CalendarLoader />
                ) : (
                    <>
                        <Box>
                            <Calendar
                                mode="accommodation"
                                handleDatesChosen={handleSetDates}
                                datesInfo={data ? data : null}
                                isLimitedRange={true}
                                rangeLimit={13}
                                isDisabled={isDisabledCalendar}
                                rangeDateStart={
                                    dates.length > 0 ? moment(dates[0]) : null
                                }
                                rangeDateEnd={
                                    dates.length > 0
                                        ? moment(dates[dates.length - 1])
                                        : null
                                }
                                testIdPrefix="accommo"
                            />
                        </Box>
                        <ModalFooter gap={16} align="flex-start" isFullWidth>
                            {!isCreatorAccommodation && (
                                <CalendarLegendItem
                                    badge="highlight"
                                    description={
                                        <FormattedMessage
                                            id="global.calendar.legend_staying_days"
                                            defaultMessage="Dni Twojego pobytu w parku"
                                        />
                                    }
                                />
                            )}
                            {isMobile && (
                                <Button
                                    onClick={confirmDates}
                                    isDisabled={unconfirmedDates.length === 0}
                                >
                                    <FormattedMessage
                                        id="global.btn.confirm"
                                        defaultMessage="Potwierdź"
                                    />
                                </Button>
                            )}
                        </ModalFooter>
                    </>
                )}
            </>
        </Modal>
    )
}

export default AccommodationCalendarModal
